import { useEffect, useMemo, useState } from "react";
import { DashboardHeader } from "@/frontend/components/DashboardHeader/ui/DashboardHeader";
import { Columns } from "./CardsColumn/ui/Columns";
import styles from "./DashboardPage.module.scss";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { postGetCompaniesData } from "../model/services/postGetCompaniesData";
import { postGetFilteredStage } from "../model/services/postGetFilteredStage";
import { defaultFilterData } from "@/frontend/components/filter/const/const";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { EContent } from "@/frontend/components/DashboardHeader/types/types";
import { ELOCAL_STORAGE_KEY } from "@/frontend/shared/const/const";
import { postPortfolioCompaniesBulk } from "../model/services/postPortfolioCompaniesBulk";
import { EDataStatus } from "@/frontend/features/Company/model/types/types";
import { TStagesResponse } from "../const/const";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { TCompanyInfo, TPipeline } from "../model/types/types";
import { InnoveraCompanyHeader } from "@/frontend/components/InnoveraCompanyHeader";
import { useMe } from "@/frontend/shared/lib/hooks/useMe";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { getPipelines } from "../model/services/getPipelines";
import { getRouteCompanies } from "@/frontend/shared/const/router";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { NoPipelinesContent } from "./NoPipelinesContent/NoPipelinesContent";
import { ConditionRowFilterExtended } from "@/frontend/components/FilterV2/model/types";
import { getAllTags } from "@/frontend/pages/CompaniesPage/model/services/getAllTags";
import { usePipelineFilters } from "@/frontend/components/FilterV2/lib/usePipelineFilters";
import { Sidebar } from "@/frontend/components/Sidebar/Sidebar";
import { Filters } from "@/frontend/components/FilterV2/ui/Filters";
import { useScreenWidth } from "@/frontend/shared/lib/hooks/useScreenWidth";
import clsx from "clsx";

export function DashboardPage() {
  const [content, setContent] = useState<EContent>(() => {
    const savedValue = localStorage.getItem(ELOCAL_STORAGE_KEY.CONTENT);
    return savedValue ? JSON.parse(savedValue) : EContent.PIPELINE;
  });
  const { data: userData } = useMe();
  const [columnPages, setColumnPages] = useState<{ stageId: number; page: number }[]>([]);
  const [currentColumnPage, setCurrentColumnPage] = useState({
    stageId: 0,
    page: 1
  });

  const savedPipeline = localStorage.getItem(ELOCAL_STORAGE_KEY.PIPELINE);
  const initialPipeline = savedPipeline ? (JSON.parse(savedPipeline) as TPipeline) : null;
  const [currentPipeline, setCurrentPipeline] = useState<TPipeline | null>(initialPipeline);

  const [filteredProcessingCompanies, setFilteredProcessingCompanies] = useState<number[]>([]);
  const [doneProcessingCompanyIds, setDoneProcessingCompanyIds] = useState<number[]>([]);

  const isMobile = useScreenWidth() <= 768;

  const [areFiltersOpen, setAreFiltersOpen] = useState(false);

  const [columnCompaniesData, setColumnCompaniesData] = useState<TStagesResponse>({
    stages: []
  });

  const [bulkDataInfo, setBulkDataInfo] = useState<TCompanyInfo[]>([]);
  const [fileUploadProgresses, setFileUploadProgresses] = useState<Record<string, number>>({});

  const queryClient = useQueryClient();

  const { filters, setFilters } = usePipelineFilters(
    ELOCAL_STORAGE_KEY.COLUMN_FILTERS + "V2",
    currentPipeline ? String(currentPipeline.id) : null
  );

  useEffect(() => {
    localStorage.setItem(ELOCAL_STORAGE_KEY.CONTENT, JSON.stringify(content));
  }, [content]);

  useEffect(() => {
    if (currentPipeline) localStorage.setItem(ELOCAL_STORAGE_KEY.PIPELINE, JSON.stringify(currentPipeline));
  }, [currentPipeline]);

  const { navigate } = useHistoricalNavigate();

  const {
    data: pipelinesData,
    isLoading: isLoadingPipelines,
    refetch: refetchPipelines
  } = useQuery({
    queryKey: ["pipelines"],
    queryFn: async () => {
      const res = await getPipelines();
      if (!currentPipeline && res?.length > 0) {
        setCurrentPipeline(res[0]);
      }
      return res;
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "DASHBOARD: /pipelines");
      snackbarConstructor("Error fetching pipelines", "error");
      navigate(getRouteCompanies());
      return true;
    }
  });

  useEffect(() => {
    if (pipelinesData) {
      const current = pipelinesData.find((pipeline) => pipeline.id === currentPipeline?.id);
      if (current) {
        setCurrentPipeline(current);
      } else {
        setCurrentPipeline(pipelinesData?.[0] || null);
      }
    }
  }, [pipelinesData]);

  const { data: tagsDataResponse } = useQuery({
    queryKey: ["tags-extra"],
    queryFn: async () => {
      const res = await getAllTags();
      return res;
    },
    refetchOnWindowFocus: false,
    gcTime: 0
  });

  const { tagFilters, staticTagFilters, restFilters } = useMemo(() => {
    const tagFilters: ConditionRowFilterExtended[] = [];
    const staticTagFilters: ConditionRowFilterExtended[] = [];
    const restFilters: ConditionRowFilterExtended[] = [];
    filters.forEach((f) => {
      if (f.type === "tag") {
        const correspondingTag = tagsDataResponse?.find((item: any) => item.name === f.value && !item.isStatic);
        if (correspondingTag) {
          tagFilters.push({ ...f, value: correspondingTag.id + "" });
        } else {
          staticTagFilters.push({ ...f, type: "staticTag" });
        }
      } else {
        restFilters.push(f);
      }
    });
    return { tagFilters, staticTagFilters, restFilters };
  }, [filters, tagsDataResponse]);

  const { isLoading, refetch } = useQuery({
    queryKey: ["allStagesDataV2", tagFilters, staticTagFilters, restFilters, currentPipeline],
    queryFn: async ({ signal }) => {
      if (!currentPipeline) return { stages: [] };

      const response = await postGetCompaniesData(
        {
          filters: [...tagFilters, ...staticTagFilters, ...restFilters],
          pipelineId: currentPipeline.id
        },
        { signal }
      );

      if (tagFilters || staticTagFilters || restFilters) {
        setFilteredProcessingCompanies(
          response?.stages?.flatMap((stage) => {
            return stage.portfolioCompanies
              .filter((company) => company.parsingStatus === EDataStatus.processing)
              .map((company) => company.id);
          })
        );
      }

      setColumnCompaniesData(response || { stages: [] });

      setColumnPages(
        response?.stages?.map((stage) => ({
          stageId: stage.id,
          page: 1
        }))
      );

      return response;
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    enabled: userData?.entity === ERoleAccess.INVESTOR && !!currentPipeline,
    throwOnError: (error) => {
      console.error(error);
      reportErrorToSentry(error, "DASHBOARD: /stages");
      return true;
    }
  });

  const { data: headersData, refetch: refetchHeaders } = useQuery({
    queryKey: ["headersDataV2", tagFilters, staticTagFilters, restFilters, currentPipeline],
    queryFn: async () =>
      await postGetCompaniesData({
        filters: [...tagFilters, ...staticTagFilters, ...restFilters],
        pipelineId: currentPipeline?.id || 0,
        noCompanies: true
      }),
    enabled: userData?.entity === ERoleAccess.INVESTOR && !!currentPipeline
  });

  const { refetch: refetchPortfolioCompaniesBulkData } = useQuery({
    queryKey: ["portfolioCompaniesBulkData", filteredProcessingCompanies, fileUploadProgresses],
    queryFn: async () => {
      const response = await postPortfolioCompaniesBulk({
        portfolioCompanyIds: filteredProcessingCompanies,
        doneProcessingCompanyIds
      });

      const resData = response.portfolioCompanies ?? response;
      setBulkDataInfo((prev) => {
        if (!response?.portfolioCompanies?.length) return prev;

        return prev.length > 0
          ? prev.map((item) => {
              const companyInfoData = resData.find((dataItem: TCompanyInfo) => dataItem.id === item.id);
              if (companyInfoData) {
                return {
                  ...item,
                  parsingStatus: companyInfoData.parsingStatus,
                  parsingProgress: companyInfoData.parsingProgress,
                  parsingMessage: companyInfoData.parsingMessage
                };
              }

              return item;
            })
          : resData;
      });

      const doneProccessingCompaniesIds = resData
        .filter((item: any) => item.parsingStatus === "matching_done" || item.parsingStatus === "done")
        .map((item: any) => item.id);

      setDoneProcessingCompanyIds(doneProccessingCompaniesIds);

      if (doneProccessingCompaniesIds.length > 0) {
        refetch();
        refetchHeaders();
      }

      if (doneProcessingCompanyIds.length > 0) {
        setDoneProcessingCompanyIds([]);
        setFilteredProcessingCompanies(
          resData
            .filter(
              (item: any) =>
                item.parsingStatus === EDataStatus.processing || item.parsingStatus === EDataStatus.matching_done
            )
            .map((item: any) => item.id)
        );

        setColumnCompaniesData((prev) => {
          const newStages = prev?.stages?.map((stage) => {
            return {
              ...stage,
              portfolioCompanies: stage.portfolioCompanies?.map((company) => {
                const updatedCompanyData = resData.find((item: any) => {
                  (item.id === company.id && item.parsingStatus === "matching_done") ||
                    (item.id === company.id && item.parsingStatus === "done");
                });
                if (updatedCompanyData) {
                  return {
                    ...company,
                    ...updatedCompanyData,
                    parsingStatus: "done"
                  };
                }
                return company;
              })
            };
          });
          return { stages: newStages };
        });
      }

      return response;
    },
    refetchOnWindowFocus: false,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    gcTime: 0,
    enabled: filteredProcessingCompanies?.length > 0 || Object.keys(fileUploadProgresses).length > 0,
    throwOnError: (error) => {
      console.error(error);

      reportErrorToSentry(error, "DASHBOARD: /portfolio-companies/bulk");
      return true;
    }
  });

  const { refetch: refetchStageData } = useQuery({
    queryKey: [
      "stageDataV2",
      currentColumnPage.stageId,
      currentColumnPage.page,
      tagFilters,
      staticTagFilters,
      restFilters
    ],
    queryFn: async () => {
      if (currentColumnPage.stageId > 0) {
        const res = await postGetFilteredStage({
          stageId: currentColumnPage.stageId,
          limit: 15,
          page: currentColumnPage.page,
          filters: [...tagFilters, ...staticTagFilters, ...restFilters]
        });

        if (!res) {
          return { stages: [] };
        }

        setColumnPages((prev) => {
          return prev.map((page) => {
            if (page.stageId === currentColumnPage.stageId) {
              return {
                ...page,
                page: currentColumnPage.page
              };
            }
            return page;
          });
        });

        setColumnCompaniesData((prev) => {
          return {
            ...prev,
            stages: prev?.stages?.map((stage) => {
              if (stage.id === currentColumnPage.stageId) {
                return {
                  ...stage,
                  portfolioCompanies: [...stage.portfolioCompanies, ...res.portfolioCompanies]
                };
              }
              return stage;
            })
          };
        });

        return res;
      }

      return { stages: [] };
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
    throwOnError: (error) => {
      console.error(error);

      reportErrorToSentry(error, "DASHBOARD: /stages/v2/${stageId}?page=${page}&limit=${limit}");
      return true;
    }
  });

  const headerOptions = useMemo(
    () =>
      columnCompaniesData.stages.map((stage) => {
        return { value: String(stage.id), label: stage.name };
      }) || [],
    [columnCompaniesData.stages]
  );

  function applyFilter() {}

  function triggerRefetch() {
    refetch();
    refetchStageData();
    queryClient.invalidateQueries({ queryKey: ["tagNames"] });
  }

  if (!isLoadingPipelines && (!pipelinesData || pipelinesData?.length == 0)) {
    return (
      <div className={styles.columnsContainer}>
        <div className={styles.columnsHeaderContainer}>
          <InnoveraCompanyHeader
            className={styles.innoveraHeader}
            activeContent={content}
            setActiveContent={setContent}
          />
        </div>
        <NoPipelinesContent
          pipelinesData={pipelinesData || []}
          refetchPipelines={refetchPipelines}
          setCurrentPipeline={setCurrentPipeline}
        />
      </div>
    );
  }

  return (
    <div className={styles.columnsContainer}>
      <div className={styles.columnsHeaderContainer}>
        <InnoveraCompanyHeader
          className={styles.innoveraHeader}
          activeContent={content}
          setActiveContent={setContent}
        />
      </div>

      <div className={styles.pageContainer}>
        <div className={clsx(styles.sidebarContainer, areFiltersOpen && !isMobile && styles.open)}>
          <Sidebar isOpen={areFiltersOpen} onClose={() => setAreFiltersOpen(false)} isMobile={isMobile}>
            <div className={styles.borderRight}>
              <Filters
                filters={filters}
                onCancel={() => setAreFiltersOpen(false)}
                onSave={(data) => {
                  setFilters(data);
                  // setAreFiltersOpen(false);
                }}
                options={{
                  tag:
                    tagsDataResponse?.map((t: any) => ({
                      label: t.name,
                      value: t.name
                    })) || []
                }}
                wrap
                noControls
                noSearch
              />
            </div>
          </Sidebar>
        </div>

        <div className={styles.contentContainer}>
          <DashboardHeader
            stageOptions={headerOptions}
            className={styles.dashboardHeader}
            onChangeContent={setContent}
            content={content}
            triggerRefetch={refetchPortfolioCompaniesBulkData}
            triggerRefetchAll={triggerRefetch}
            setBulkDataInfo={setBulkDataInfo}
            numFilters={filters.length}
            setFileUploadProgresses={setFileUploadProgresses}
            currentPipeline={currentPipeline}
            setCurrentPipeline={setCurrentPipeline}
            pipelinesData={pipelinesData}
            isLoadingPipelines={isLoadingPipelines}
            refetchPipelines={refetchPipelines}
            setFiltersOpen={setAreFiltersOpen}
            filtersOpen={areFiltersOpen}
          />
          {columnCompaniesData && !isLoading && !!currentPipeline ? (
            <Columns
              data={columnCompaniesData}
              triggerRefetch={triggerRefetch}
              triggerRefetchBulkData={refetchPortfolioCompaniesBulkData}
              applyFilter={applyFilter}
              refetchStageData={refetchStageData}
              setCurrentPageForStage={setCurrentColumnPage}
              currentPages={columnPages}
              setFileUploadProgresses={setFileUploadProgresses}
              setBulkDataInfo={setBulkDataInfo}
              setColumnCompaniesData={setColumnCompaniesData}
              portfolioCompaniesBulkData={bulkDataInfo || []}
              headersData={headersData}
              refetchHeaders={refetchHeaders}
              filteredProcessingCompanies={filteredProcessingCompanies}
              fileUploadProgresses={fileUploadProgresses}
              setDashboardContent={setContent}
              numFilters={filters.length}
              refetchPipelines={refetchPipelines}
            />
          ) : (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
